/**
 * Komal OS - CRM: sales dashboard.
 *
 * Open deals by stage/owner, and leads captured vs. converted per event
 * (spec section 7). Scoped server-side the same as everywhere else in
 * this module - owner sees their own numbers, CEO sees everyone's (see
 * app/routes/crm_routes.py's get_dashboard).
 *
 * Deliberately does NOT show one converted pipeline total across
 * currencies - see get_dashboard's docstring: this app has no FX rate
 * source, so open value is shown per currency instead of a made-up total.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Layers, Percent, BellRing } from 'lucide-react';
import { getDashboard } from '../../services/crmService';
import GeographyPanel from '../../components/crm/GeographyPanel';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';

const STAGE_LABELS = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', quoted: 'Quoted', won: 'Won', lost: 'Lost' };
const STAGE_COLOR = { new: '#60A5FA', contacted: '#3B82F6', qualified: '#A78BFA', quoted: '#5E1041', won: '#22C55E', lost: '#EF4444' };

const formatMoney = (currency, value) => `${currency} ${Number(value).toLocaleString()}`;

export default function CrmDashboardPage() {
  const { user } = useAuthStore();
  const { showToast } = useUIStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboard()
      .then((res) => setData(res.data))
      .catch((err) => showToast(err.response?.data?.detail || 'Could not load the dashboard', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="min-h-screen p-6 text-white/80 text-sm">Loading…</div>;
  if (!data) return <div className="min-h-screen p-6 text-white/80 text-sm">Could not load the dashboard.</div>;

  const { stats, by_stage: byStage, by_owner: byOwner, by_event: byEvent } = data;
  const maxStageCount = Math.max(1, ...byStage.map((s) => s.count));
  const currencies = Object.entries(stats.open_value_by_currency);
  const maxEventContacts = Math.max(1, ...byEvent.map((e) => e.contacts_captured));

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white">Sales dashboard</h1>
          <p className="text-sm text-white/70 mt-0.5">
            {user?.role === 'CEO' ? 'Open pipeline and exhibition conversion, across the team.' : 'Your open pipeline and exhibition conversion.'}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-5">
          <div className="glass-card p-4">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1"><Layers size={13} /><span className="text-[11px] font-semibold uppercase tracking-wide">Open deals</span></div>
            <div className="text-2xl font-extrabold text-gray-800 tabular-nums">{stats.open_deals}</div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1"><TrendingUp size={13} /><span className="text-[11px] font-semibold uppercase tracking-wide">Open pipeline value</span></div>
            {currencies.length === 0 ? (
              <div className="text-2xl font-extrabold text-gray-300 tabular-nums">—</div>
            ) : (
              <div className="space-y-0.5">
                {currencies.map(([cur, val]) => (
                  <div key={cur} className="text-lg font-extrabold text-gray-800 tabular-nums">{formatMoney(cur, val)}</div>
                ))}
              </div>
            )}
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1"><Percent size={13} /><span className="text-[11px] font-semibold uppercase tracking-wide">Qualified → Quoted</span></div>
            <div className="text-2xl font-extrabold text-gray-800 tabular-nums">
              {stats.qualified_to_quoted_rate == null ? '—' : `${Math.round(stats.qualified_to_quoted_rate)}%`}
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1"><BellRing size={13} /><span className="text-[11px] font-semibold uppercase tracking-wide">Reminders due soon</span></div>
            <div className="text-2xl font-extrabold text-gray-800 tabular-nums">{stats.reminders_due_soon}</div>
          </div>
        </div>

        <div className="glass-card p-5 mb-5">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Open deals by stage</div>
          <div className="flex items-end gap-4 h-40 border-b border-gray-100 px-1">
            {byStage.map((s) => (
              <div key={s.stage} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
                <span className="text-xs font-semibold text-gray-700 mb-1 tabular-nums">{s.count}</span>
                <div
                  className="w-full rounded-t"
                  style={{ height: `${Math.max((s.count / maxStageCount) * 100, s.count > 0 ? 4 : 0)}%`, backgroundColor: STAGE_COLOR[s.stage] }}
                />
                <span className="text-[11px] text-gray-500 mt-1.5">{STAGE_LABELS[s.stage]}</span>
              </div>
            ))}
          </div>
        </div>

        <GeographyPanel />

        <div className="grid gap-5 md:grid-cols-2">
          <div className="glass-card p-5">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">By owner</div>
            {byOwner.length === 0 ? (
              <p className="text-xs text-gray-400">No deals yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                    <th className="pb-2 font-semibold">Owner</th>
                    <th className="pb-2 font-semibold text-right">Open</th>
                    <th className="pb-2 font-semibold text-right">Won</th>
                    <th className="pb-2 font-semibold text-right">Lost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byOwner.map((o) => (
                    <tr key={o.owner?.id || 'unassigned'}>
                      <td className="py-2 font-medium text-gray-800">{o.owner?.name || 'Unassigned'}</td>
                      <td className="py-2 text-right tabular-nums text-gray-600">{o.open_deals}</td>
                      <td className="py-2 text-right tabular-nums text-green-600">{o.won}</td>
                      <td className="py-2 text-right tabular-nums text-red-500">{o.lost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="glass-card p-5">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Leads captured vs. converted, by exhibition</div>
            {byEvent.length === 0 ? (
              <p className="text-xs text-gray-400">No exhibition-sourced leads yet.</p>
            ) : (
              <div className="space-y-3">
                {byEvent.map((row) => (
                  <div key={row.event.id}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700 truncate">{row.event.name}</span>
                      <span className="text-gray-400 tabular-nums">{row.deals_won} won / {row.contacts_captured} captured</span>
                    </div>
                    <div className="flex gap-0.5 h-2.5">
                      <div
                        className="rounded-l bg-green-500"
                        style={{ width: `${Math.max((row.deals_won / maxEventContacts) * 100, row.deals_won > 0 ? 2 : 0)}%` }}
                      />
                      <div
                        className="rounded-r bg-gray-200"
                        style={{ width: `${Math.max(((row.contacts_captured - row.deals_won) / maxEventContacts) * 100, 2)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-white/50 mt-5">
          Want the full picture? <Link to="/crm/pipeline" className="underline">Open the pipeline</Link> or <Link to="/crm/accounts" className="underline">browse customers</Link>.
        </p>
      </div>
    </div>
  );
}
